import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import AttributeLabel from './AttributeLabel';
import { ROLE_ICONS, ROLE_ICON_KEYS, getRoleIconComponent } from '../utils/roleIcons';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import type { ModalCloseReason } from './modals/core';

interface Role {
  id: number;
  name: string;
  description: string | null;
  priority: number;
  icon: string | null;
  member_count: number;
}

// Rollen, die im Code verankert sind und nicht gelöscht/umbenannt werden dürfen
const SYSTEM_ROLES = ['guest', 'member', 'trainer', 'auditor', 'board'];

interface RoleManagementModalProps {
  onClose: () => void;
  onRolesChanged?: () => void;
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const RoleManagementModal: React.FC<RoleManagementModalProps> = ({ onClose, onRolesChanged }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ description: '', name: '', priority: 0, icon: 'user' });

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  const fetchRoles = async () => {
    try {
      const response = await apiClient.get('/api/roles/available');
      if (response.data.success && Array.isArray(response.data.roles)) {
        setRoles(response.data.roles);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Rollen:', err);
      setError('Rollen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const openCreateForm = () => {
    setEditingRole(null);
    setFormData({ description: '', name: '', priority: 0, icon: 'user' });
    setError(null);
    setShowForm(true);
  };

  const openEditForm = (role: Role) => {
    setEditingRole(role);
    setFormData({
      description: role.description || '',
      name: role.name,
      priority: role.priority || 0,
      icon: role.icon || '',
    });
    setError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.description.trim()) {
      setError('Bitte eine Bezeichnung angeben');
      return;
    }
    const technicalName = editingRole
      ? editingRole.name
      : (formData.name.trim() || slugify(formData.description));
    if (!technicalName) {
      setError('Bitte einen technischen Namen angeben');
      return;
    }
    if (!editingRole && roles.some(r => r.name === technicalName)) {
      setError(`Der technische Name "${technicalName}" ist bereits vergeben`);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: technicalName,
        description: formData.description.trim(),
        priority: formData.priority,
        icon: formData.icon || null,
      };
      if (editingRole) {
        await apiClient.put(`/api/roles/${editingRole.id}`, payload);
      } else {
        await apiClient.post('/api/roles', payload);
      }
      setShowForm(false);
      setEditingRole(null);
      await fetchRoles();
      onRolesChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: Role) => {
    if (confirmDeleteId !== role.id) {
      setConfirmDeleteId(role.id);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiClient.delete(`/api/roles/${role.id}`);
      setConfirmDeleteId(null);
      await fetchRoles();
      onRolesChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Löschen fehlgeschlagen');
      setConfirmDeleteId(null);
    } finally {
      setSaving(false);
    }
  };

  const footer = showForm ? (
    <ModalActions
      secondary={{
        label: 'Abbrechen',
        onClick: () => { setShowForm(false); setError(null); },
      }}
      primary={{
        label: editingRole ? 'Speichern' : 'Rolle anlegen',
        onClick: handleSave,
        disabled: saving || !formData.description.trim(),
        loading: saving,
      }}
    />
  ) : (
    <ModalActions
      secondary={{ label: 'Schließen', onClick: onClose }}
      primary={{ label: 'Neue Rolle', onClick: openCreateForm, icon: <PlusIcon className="h-4 w-4" /> }}
    />
  );

  return (
    <ModalShell
      open
      onClose={handleClose}
      variant="form"
      size="md"
      accent="brand"
      icon={ShieldCheckIcon}
      title="Rollenverwaltung"
      subtitle={`${roles.length} Rollen definiert`}
      closeOnBackdrop={false}
      footer={footer}
    >
      <div className="space-y-3">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 text-sm text-error-700 dark:text-error-300">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
            Rollen werden geladen...
          </div>
        ) : showForm ? (
          /* Formular: Rolle anlegen / bearbeiten */
          <div className="space-y-4">
            <div>
              <AttributeLabel iconKey="description" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                Bezeichnung
              </AttributeLabel>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="z.B. Kassenwart"
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <AttributeLabel iconKey="name" className="block mb-1" textClassName="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Technischer Name
                </AttributeLabel>
                <input
                  type="text"
                  value={editingRole ? editingRole.name : (formData.name || slugify(formData.description))}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: slugify(e.target.value) }))}
                  disabled={!!editingRole}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priorität
                </label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Icon-Auswahl */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon
              </label>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                {ROLE_ICON_KEYS.map((key) => {
                  const IconComponent = ROLE_ICONS[key].component;
                  const isSelected = formData.icon === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, icon: key }))}
                      title={ROLE_ICONS[key].label}
                      className={`aspect-square rounded-xl flex items-center justify-center border transition-colors ${
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-500 dark:border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Rollen-Liste */
          <ModalSection>
            <div className="space-y-3">
              {roles.map((role) => {
                const IconComponent = getRoleIconComponent(role);
                const isSystemRole = SYSTEM_ROLES.includes(role.name);
                const canDelete = !isSystemRole && role.member_count === 0;
                return (
                  <div
                    key={role.id}
                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {role.description || role.name}
                        </span>
                        {isSystemRole && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                            System
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {role.name} · Priorität {role.priority} · {role.member_count} {role.member_count === 1 ? 'Mitglied' : 'Mitglieder'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        onClick={() => openEditForm(role)}
                        className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-colors"
                        title="Bearbeiten"
                      >
                        <PencilIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                      {confirmDeleteId === role.id ? (
                        <button
                          onClick={() => handleDelete(role)}
                          disabled={saving}
                          className="h-9 px-3 rounded-lg bg-error-600 hover:bg-error-700 flex items-center gap-1.5 transition-colors"
                          title="Löschen bestätigen"
                        >
                          <CheckIcon className="w-4 h-4 text-white" />
                          <span className="text-xs font-semibold text-white">Sicher?</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => canDelete && handleDelete(role)}
                          disabled={!canDelete}
                          className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                            canDelete
                              ? 'bg-error-50 dark:bg-error-900/20 hover:bg-error-100 dark:hover:bg-error-900/40'
                              : 'bg-gray-100 dark:bg-gray-700 opacity-40 cursor-not-allowed'
                          }`}
                          title={
                            isSystemRole
                              ? 'System-Rollen können nicht gelöscht werden'
                              : role.member_count > 0
                                ? `Noch ${role.member_count} Mitglied(ern) zugeordnet`
                                : 'Rolle löschen'
                          }
                        >
                          <TrashIcon className={`w-4 h-4 ${canDelete ? 'text-error-600 dark:text-error-400' : 'text-gray-400'}`} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ModalSection>
        )}
      </div>
    </ModalShell>
  );
};

export default RoleManagementModal;
